/**
 * 主题（外观）面板 —— **N 域** · 用户级 · 设置页里写 `user_preferences.theme` 的唯一 UI
 *
 * 依据：N 域 PRD **§5.3 N3**「主题三态 + 持久化服务端」（`AC-N3.1` / `AC-N3.2` / `AC-N3.3`）·
 * 原型 `08-家庭与设置_原型.html` 的「外观」卡片（`.segmented` 三按钮：浅色 / 深色 / 跟随系统）。
 *
 * ## 🔴 三条 UI 纪律
 *
 * | # | 纪律 | 本组件怎么做的 |
 * |---|---|---|
 * | ① | **作用域显式标注**（`DR-020` 层级铁律） | 头部带 `ScopeTag「用户级」` + 一句「家人不受影响」 |
 * | ② | 🔴 **把"跟随系统"的解析结果说出来** | `themeEffectiveNote()`：「当前按你的系统显示：深色」—— 否则选了跟随系统的用户会以为设置坏了 |
 * | ③ | **端侧不写解析** | 三态 → 明/暗 一律调 `resolveThemeMode()`（`theme/theme.ts` 唯一实现） |
 *
 * ## 🔴 为什么**没有「保存」按钮**（与提醒偏好面板不同）
 *
 * 三态是**互斥单选**，一次点击就是一次完整决定（没有"改到一半"的中间态）⇒ 点击即保存。
 * 提醒偏好那张卡之所以要保存按钮，是因为它是一组**可组合**的开关（改到一半时不该落库）。
 *
 * ## ⚠️ 本批**不含**（诚实登记）
 *
 * - **小程序主题**：P0 跟随系统（`DR-019` C2），手动开关 P1 —— 且小程序端目前是空壳；
 * - **深色全页覆盖走查**（`AC-N3.7`）：属全局视觉走查，非单一组件可证（见 changelog 登记）。
 */

import { useState } from 'react';
import { Alert, Box, Card, CardContent, Stack, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ThemeValue } from '@jiaying/shared';
import { getPreferences, updatePreferences } from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import { useApp } from '../app/store.js';
import { resolveThemeMode } from '../theme/theme.js';
import { Loading } from './Loading.js';
import { ScopeTag } from './ScopeTag.js';
import {
  BRAND_DESC,
  BRAND_READONLY_LABEL,
  BRAND_TITLE,
  SAVE_FAILED_PREFIX,
  SAVED_LABEL,
  SECTION_NOTE,
  SECTION_TITLE,
  THEME_DESC,
  THEME_OPTIONS,
  THEME_SCOPE_NOTE,
  THEME_TITLE,
  themeEffectiveNote,
} from '../features/theme-labels.js';

export function ThemePreferencePanel() {
  const qc = useQueryClient();
  const tk = useTheme().jiaying.tokens;
  const { systemPrefersDark, applyServerTheme } = useApp();
  const q = useQuery({ queryKey: ['preferences'], queryFn: getPreferences });

  /**
   * 用户刚点的那一档（`null` = 还没点过 ⇒ 显示服务端值）。
   *
   * 🔴 只能在**渲染期**派生（`override ?? 服务端值`）—— `renderToStaticMarkup`
   * 不执行 effect，把初值只放在 effect 里会让首屏永远停在 Loading（`57` 批踩过，铁律 47 同族）。
   */
  const [override, setOverride] = useState<ThemeValue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: (theme: ThemeValue) => updatePreferences({ theme }),
    onSuccess: (res) => {
      setOverride(res.theme);
      // 🔴 保存后把服务端权威值**立刻**灌回 store（不等 Root 的 useThemeSync 再取一次）
      applyServerTheme(res.theme);
      setError(null);
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['preferences'] });
    },
    onError: (e: unknown) => {
      // 失败 ⇒ 撤销乐观选择（回到服务端值），并明说失败（不静默回到原样）
      setOverride(null);
      setSaved(false);
      setError(e instanceof ApiError ? e.message : String(e));
    },
  });

  if (q.isPending) return <Loading py={2} />;
  if (q.isError) return <Alert severity="error">偏好读取失败：{String(q.error)}</Alert>;

  const current: ThemeValue = override ?? q.data.theme;
  const effective = resolveThemeMode(current, systemPrefersDark);

  const pick = (next: ThemeValue): void => {
    if (next === current) return;
    setSaved(false);
    setError(null);
    setOverride(next); // 乐观：先让选中态与主题都动，再落库
    applyServerTheme(next);
    save.mutate(next);
  };

  return (
    <Card data-anchor={SECTION_TITLE} data-testid="theme-panel">
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {SECTION_TITLE}
          </Typography>
          <ScopeTag scope="用户级" />
        </Stack>
        <Typography variant="caption" color="text.secondary" component="div">
          {SECTION_NOTE}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div" data-testid="theme-scope-note">
          {THEME_SCOPE_NOTE}
        </Typography>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ mt: 1.5 }}
        >
          <Stack>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {THEME_TITLE}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {THEME_DESC}
            </Typography>
          </Stack>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={current}
            onChange={(_e, v: ThemeValue | null) => {
              if (v !== null) pick(v);
            }}
            aria-label={THEME_TITLE}
            data-testid="theme-group"
          >
            {THEME_OPTIONS.map((o) => (
              <ToggleButton
                key={o.id}
                value={o.id}
                disabled={save.isPending}
                data-testid={`theme-opt-${o.id}`}
              >
                {o.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>

        {/* 🔴 把"跟随系统"的解析结果说出来（纪律 ②） */}
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ mt: 0.5 }}
          data-testid="theme-effective"
        >
          {themeEffectiveNote(current, effective)}
        </Typography>

        {saved ? (
          <Typography variant="caption" color="success.main" component="div" data-testid="theme-saved">
            {SAVED_LABEL}
          </Typography>
        ) : null}
        {error !== null ? (
          <Alert severity="error" sx={{ mt: 1 }} data-testid="theme-error">
            {SAVE_FAILED_PREFIX}
            {error}
          </Alert>
        ) : null}

        {/* ── 品牌色（原型原文：**仅展示 · 不可选** —— 铁律 9/10：token 单源，改色必过对比度实算） ── */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mt: 1.5 }}>
          <Stack>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {BRAND_TITLE}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {BRAND_DESC}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {['em-500', 'em-700', 'em-300'].map((name) => (
              <Box
                key={name}
                data-testid={`brand-swatch-${name}`}
                sx={{ width: 24, height: 24, borderRadius: '7px', bgcolor: tk[name] }}
              />
            ))}
            <Typography variant="caption" color="text.secondary" data-testid="brand-readonly">
              {BRAND_READONLY_LABEL}
            </Typography>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
